/**
 * Event batcher
 *
 * Collects events that arrive one at a time and sends them as one grouped
 * event. Built for view_item_list: the Pixel Manager detects every product
 * the moment it has been on screen long enough, but GA4, Google Ads, Klaviyo,
 * Mixpanel and Criteo are better served with one event per product list.
 *
 * Detection and sending are therefore two separate moments, and both are
 * logged: the platform layer logs the detection, the batcher logs every
 * product it queues and every grouped event it sends, with the reason.
 *
 * A batch is sent when the first of these happens:
 * - no product was added for idleTimeout ms,
 * - maxWait ms passed since the first product of the batch was queued,
 * - the batch holds maxItems products,
 * - the visitor leaves or hides the page (pagehide, visibilitychange),
 * - any other Pixel Manager event is processed (see pmw.processEvent), so a
 *   pixel always receives the list view before the select_item or
 *   add_to_cart that followed it.
 *
 * The page-leave listeners are registered on window in the capture phase.
 * Capture listeners on the target run before bubble-phase ones, and
 * visibilitychange is dispatched on document, so window capture runs before
 * the listeners gtag.js registers to flush its own queue. The grouped event
 * is handed to the vendor library while it can still send it with beacon or
 * keepalive transport.
 *
 * Platform neutral: no WooCommerce, WordPress or jQuery code in here.
 *
 * @since 1.71.1
 */

(function (pmw, undefined) {

	const batchers = [];

	let leaveListenersAdded = false;

	/**
	 * Send every open batch of every batcher right now.
	 *
	 * @param {string} reason Why the batches are sent, for the log
	 */
	pmw.flushEventBatchers = (reason = "flush") => {

		batchers.forEach(batcher => {
			try {
				batcher.flush(reason);
			} catch (e) {
				console.error(e);
			}
		});
	};

	const addLeaveListeners = () => {

		if (leaveListenersAdded) return;
		leaveListenersAdded = true;

		window.addEventListener("pagehide", () => pmw.flushEventBatchers("page leave"), true);

		window.addEventListener("visibilitychange", () => {
			if (document.visibilityState === "hidden") pmw.flushEventBatchers("page hidden");
		}, true);
	};

	/**
	 * Create a batcher.
	 *
	 * @param {object}   options
	 * @param {string}   options.label       Pixel and event, used in the log ("Google Analytics: view_item_list")
	 * @param {Function} options.send        Called with (items, meta, reason) for every grouped event
	 * @param {Function} [options.getConfig] Returns {enabled, idleTimeout, maxWait, maxItems}, read on every add
	 * @param {Function} [options.itemKey]   Returns a key per item; an item whose key is already in the batch is dropped
	 * @returns {{add: Function, flush: Function, size: Function}}
	 */
	pmw.createEventBatcher = ({label, send, getConfig = () => ({}), itemKey = null}) => {

		// One open batch per group key, e.g. per GA4 measurement ID and product list
		const batches = new Map();

		const sendBatch = (groupKey, reason) => {

			const batch = batches.get(groupKey);
			if (!batch) return;

			batches.delete(groupKey);
			clearTimeout(batch.idleTimer);
			clearTimeout(batch.maxWaitTimer);

			if (!batch.items.length) return;

			pmw.console.log(`${label} sent ${batch.items.length} ${batch.items.length === 1 ? "item" : "items"} as one event (${reason})`, {
				group: groupKey,
				items: batch.items,
				meta : batch.meta,
			});

			send(batch.items, batch.meta, reason);
		};

		const batcher = {

			/**
			 * Queue one item.
			 *
			 * @param {string} groupKey Items with the same key are sent together
			 * @param {*}      item     The item to queue
			 * @param {object} meta     Event-level data of the group, e.g. the list name. The first add of a batch sets it.
			 */
			add(groupKey, item, meta = {}) {

				const config = Object.assign({
					enabled    : true,
					idleTimeout: 1500,
					maxWait    : 10000,
					maxItems   : 25,
				}, getConfig() || {});

				if (!config.enabled) {
					pmw.console.log(`${label} sent 1 item (batching off)`, {group: groupKey, item, meta});
					send([item], meta, "batching off");
					return;
				}

				addLeaveListeners();

				let batch = batches.get(groupKey);

				if (!batch) {
					batch = {items: [], keys: new Set(), meta};
					batches.set(groupKey, batch);
					batch.maxWaitTimer = setTimeout(() => sendBatch(groupKey, "max wait"), config.maxWait);
				}

				const key = itemKey ? itemKey(item) : null;

				if (key !== null && batch.keys.has(key)) {
					pmw.console.log(`${label} already queued, skipped`, {group: groupKey, item});
					return;
				}

				if (key !== null) batch.keys.add(key);
				batch.items.push(item);

				pmw.console.log(`${label} queued (${batch.items.length} in batch)`, {group: groupKey, item});

				if (batch.items.length >= config.maxItems) {
					sendBatch(groupKey, "max items");
					return;
				}

				clearTimeout(batch.idleTimer);
				batch.idleTimer = setTimeout(() => sendBatch(groupKey, "idle"), config.idleTimeout);
			},

			/**
			 * Send all open batches of this batcher.
			 *
			 * @param {string} reason
			 */
			flush(reason = "flush") {
				Array.from(batches.keys()).forEach(groupKey => sendBatch(groupKey, reason));
			},

			/**
			 * Number of queued items over all open batches, for tests and diagnostics.
			 *
			 * @returns {number}
			 */
			size() {
				let size = 0;
				batches.forEach(batch => size += batch.items.length);
				return size;
			},
		};

		batchers.push(batcher);

		return batcher;
	};

	/**
	 * Batching settings for view_item_list, from the view_item_list_trigger
	 * settings the pmw_view_item_list_trigger_settings filter controls.
	 *
	 * @returns {{enabled: boolean, idleTimeout: number, maxWait: number, maxItems: number}}
	 */
	pmw.getViewItemListBatchConfig = () => {

		const settings = pmwDataLayer?.shop?.view_item_list_trigger || {};

		const number = (value, fallback) => {
			const parsed = Number(value);
			return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
		};

		return {
			enabled    : settings.batching !== false,
			idleTimeout: number(settings.batch_idle_timeout, 1500),
			maxWait    : number(settings.batch_max_wait, 10000),
			maxItems   : number(settings.batch_max_items, 25),
		};
	};

}(window.pmw = window.pmw || {}));
